import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Scale, BarChart3, ArrowLeftRight, User } from "lucide-react";
import {
  testRunsCompareTestRuns,
  testRunsListTestRuns,
  testRunsGetTestRun,
  evaluationsListEvaluations,
} from "../../gen";
import type {
  TestRunComparisonResponse,
  TestRunResponse,
  TestRunListResponse,
  TestCaseComparison,
  EvaluationResponse,
} from "../../gen/models";
import { useOrg } from "../../hooks/useOrg";
import { useToast } from "../../context/ToastContext";
import { extractListItems } from "../../components/thread/threadUtils";

interface CategoryScoreData {
  avg: number;
  dist: Record<number, number>;
  count: number;
}

export function RunComparisonWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeOrgId } = useOrg();
  const { toast } = useToast();

  const runAIdParam = searchParams.get("run_a_id") || "";
  const runBIdParam = searchParams.get("run_b_id") || "";

  const [availableRuns, setAvailableRuns] = useState<TestRunResponse[]>([]);
  const [runAId, setRunAId] = useState(runAIdParam);
  const [runBId, setRunBId] = useState(runBIdParam);
  const [comparison, setComparison] =
    useState<TestRunComparisonResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const [evalsA, setEvalsA] = useState<EvaluationResponse[]>([]);
  const [evalsB, setEvalsB] = useState<EvaluationResponse[]>([]);
  const [runADetail, setRunADetail] = useState<TestRunResponse | null>(null);
  const [runBDetail, setRunBDetail] = useState<TestRunResponse | null>(null);
  const [loadingEvals, setLoadingEvals] = useState(false);

  useEffect(() => {
    const fetchRuns = async () => {
      try {
        const resp = await testRunsListTestRuns({ limit: 100 });
        const data = (resp as any)?.data ?? resp;
        if (data && "runs" in data) {
          const listResp = data as TestRunListResponse;
          const completedRuns = listResp.runs.filter(
            (r) => r.status === "completed",
          );
          setAvailableRuns(completedRuns);

          if (!runAId && completedRuns.length > 0) {
            setRunAId(completedRuns[0].id);
          }
          if (!runBId && completedRuns.length > 1) {
            setRunBId(completedRuns[1].id);
          }
        }
      } catch (err: any) {
        toast.error(err.message, "Failed to load runs");
      }
    };
    fetchRuns();
  }, [activeOrgId]);

  const loadComparison = async (a: string, b: string) => {
    if (!a || !b || a === b) return;
    setLoading(true);
    setLoadingEvals(true);
    try {
      const resp = await testRunsCompareTestRuns({ run_a_id: a, run_b_id: b });
      const data = (resp as any)?.data ?? resp;
      if (data && "run_a_id" in data) {
        const comp = data as TestRunComparisonResponse;
        setComparison(comp);
        setSearchParams({ run_a_id: a, run_b_id: b });

        // Fetch evaluations for Run A and Run B batches concurrently
        const fetchEvalsForBatch = async (batchId?: string | null) => {
          if (!batchId) return [];
          try {
            const res = await evaluationsListEvaluations({
              batch_id: batchId,
              include_test: true,
              limit: 200,
            });
            return extractListItems<EvaluationResponse>(res);
          } catch {
            return [];
          }
        };

        const [resA, resB, runDetailsA, runDetailsB] = await Promise.all([
          fetchEvalsForBatch(comp.feedback_batch_a_id),
          fetchEvalsForBatch(comp.feedback_batch_b_id),
          testRunsGetTestRun(a).catch(() => null),
          testRunsGetTestRun(b).catch(() => null),
        ]);
        setEvalsA(resA);
        setEvalsB(resB);
        setRunADetail(
          ((runDetailsA as any)?.data ?? runDetailsA) as TestRunResponse | null,
        );
        setRunBDetail(
          ((runDetailsB as any)?.data ?? runDetailsB) as TestRunResponse | null,
        );
      }
    } catch (err: any) {
      toast.error(
        err.message || "Failed to compare test runs",
        "Comparison failed",
      );
      setComparison(null);
      setEvalsA([]);
      setEvalsB([]);
      setRunADetail(null);
      setRunBDetail(null);
    } finally {
      setLoading(false);
      setLoadingEvals(false);
    }
  };

  useEffect(() => {
    if (runAId && runBId && runAId !== runBId) {
      loadComparison(runAId, runBId);
    }
  }, [runAId, runBId]);

  return (
    <div className="flex-1 flex flex-col h-full overflow-y-auto bg-stone-50 dark:bg-stone-950 p-6">
      <div className="max-w-6xl w-full mx-auto space-y-6">
        {/* Header & Run Selectors */}
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h1 className="text-xl font-bold text-stone-900 dark:text-stone-100">
              Prompt & Knowledge A/B Run Comparison
            </h1>
          </div>
          <p className="text-xs text-stone-500 mb-6">
            Compare per-case side-by-side agent output between Baseline (Run A)
            and Candidate (Run B).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-4 items-center">
            {/* Run A (Baseline) */}
            <div className="p-4 bg-stone-50 dark:bg-stone-950 rounded-xl border border-stone-200 dark:border-stone-800">
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider block mb-2">
                Baseline (Run A)
              </span>
              <select
                value={runAId}
                onChange={(e) => setRunAId(e.target.value)}
                className="w-full text-xs p-2.5 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 font-mono"
              >
                <option value="">Select baseline run...</option>
                {availableRuns.map((r) => (
                  <option key={r.id} value={r.id} disabled={r.id === runBId}>
                    {r.name || `${r.suite_name || "Suite"} — ${r.snapshot_name || "Snapshot"}`} ({r.id.slice(0, 8)})
                  </option>
                ))}
              </select>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center -my-1 md:my-0">
              <button
                type="button"
                onClick={() => {
                  const prevA = runAId;
                  const prevB = runBId;
                  setRunAId(prevB);
                  setRunBId(prevA);
                }}
                disabled={!runAId || !runBId}
                title="Swap runs and comparison order"
                aria-label="Swap runs and comparison order"
                className="p-2.5 rounded-full border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-600 dark:text-stone-300 hover:text-indigo-600 dark:hover:text-indigo-400 shadow-sm transition disabled:opacity-40 disabled:pointer-events-none"
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>
            </div>

            {/* Run B (Candidate) */}
            <div className="p-4 bg-indigo-50/50 dark:bg-indigo-950/20 rounded-xl border border-indigo-200 dark:border-indigo-900/50">
              <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider block mb-2">
                Candidate (Run B)
              </span>
              <select
                value={runBId}
                onChange={(e) => setRunBId(e.target.value)}
                className="w-full text-xs p-2.5 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 font-mono"
              >
                <option value="">Select candidate run...</option>
                {availableRuns.map((r) => (
                  <option key={r.id} value={r.id} disabled={r.id === runAId}>
                    {r.name || `${r.suite_name || "Suite"} — ${r.snapshot_name || "Snapshot"}`} ({r.id.slice(0, 8)})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Comparison Details */}
        {loading ? (
          <div className="p-12 text-center text-sm text-stone-500">
            Computing comparison metrics...
          </div>
        ) : comparison ? (
          <div className="space-y-6">
            {/* Run Baseline & Candidate Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-stone-900 p-5 rounded-xl border border-stone-200 dark:border-stone-800">
                <div className="text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Run A Baseline
                </div>
                <div
                  className="text-sm font-semibold text-stone-900 dark:text-stone-100 mt-2 truncate"
                  title={runADetail?.name || comparison.snapshot_a_name || "Baseline"}
                >
                  {runADetail?.name || comparison.snapshot_a_name || "Baseline"}
                </div>
                <div className="text-[11px] font-mono text-stone-400 mt-0.5">
                  {runADetail?.name ? `${comparison.snapshot_a_name || "Baseline"} • ` : ""}ID: {comparison.run_a_id.slice(0, 8)}
                </div>
              </div>

              <div className="bg-white dark:bg-stone-900 p-5 rounded-xl border border-stone-200 dark:border-stone-800">
                <div className="text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Run B Candidate
                </div>
                <div
                  className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mt-2 truncate"
                  title={runBDetail?.name || comparison.snapshot_b_name || "Candidate"}
                >
                  {runBDetail?.name || comparison.snapshot_b_name || "Candidate"}
                </div>
                <div className="text-[11px] font-mono text-stone-400 mt-0.5">
                  {runBDetail?.name ? `${comparison.snapshot_b_name || "Candidate"} • ` : ""}ID: {comparison.run_b_id.slice(0, 8)}
                </div>
              </div>
            </div>

            {/* Side-by-side Score Summary Charts (Run A vs Run B) */}
            {(() => {
              const calcCategoryData = (
                evals: EvaluationResponse[],
                getter: (e: EvaluationResponse) => number | null | undefined,
              ): CategoryScoreData | null => {
                const values = evals
                  .map(getter)
                  .filter(
                    (v): v is number => typeof v === "number" && !isNaN(v),
                  );
                if (values.length === 0) return null;
                const avg = values.reduce((a, b) => a + b, 0) / values.length;
                const dist: Record<number, number> = {
                  1: 0,
                  2: 0,
                  3: 0,
                  4: 0,
                  5: 0,
                };
                for (const v of values) {
                  const bucket = Math.max(1, Math.min(5, Math.round(v)));
                  dist[bucket] = (dist[bucket] || 0) + 1;
                }
                return { avg, dist, count: values.length };
              };

              const categories = [
                {
                  key: "composite",
                  label: "Overall Score",
                  getter: (e: EvaluationResponse) => e.score,
                },
                {
                  key: "accuracy",
                  label: "Accuracy",
                  getter: (e: EvaluationResponse) => e.accuracy_score,
                },
                {
                  key: "instruction_adherence",
                  label: "Instruction Adherence",
                  getter: (e: EvaluationResponse) =>
                    e.instruction_adherence_score,
                },
                {
                  key: "tone",
                  label: "Tone & Courtesy",
                  getter: (e: EvaluationResponse) => e.tone_score,
                },
                {
                  key: "tool_efficiency",
                  label: "Tool Efficiency",
                  getter: (e: EvaluationResponse) => e.tool_efficiency_score,
                },
              ];

              const renderedCats = categories
                .map((cat) => ({
                  ...cat,
                  dataA: calcCategoryData(evalsA, cat.getter),
                  dataB: calcCategoryData(evalsB, cat.getter),
                }))
                .filter((c) => c.dataA !== null || c.dataB !== null);

              if (loadingEvals) {
                return (
                  <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-sm text-center text-xs text-stone-500">
                    Loading evaluations for side-by-side score summary...
                  </div>
                );
              }

              if (renderedCats.length === 0) {
                return (
                  <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="w-4 h-4 text-stone-500" />
                      <h3 className="text-xs font-semibold text-stone-700 dark:text-stone-300 uppercase tracking-wider">
                        Score Summary & Distribution
                      </h3>
                    </div>
                    <p className="text-xs text-stone-400">
                      No evaluations recorded yet for the feedback batches of
                      these test runs. Run review rounds or automated
                      evaluations to populate score distributions.
                    </p>
                  </div>
                );
              }

              return (
                <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-sm space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-stone-100 dark:border-stone-800">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      <h3 className="text-xs font-semibold text-stone-700 dark:text-stone-300 uppercase tracking-wider">
                        Side-by-Side Score Summary & Distributions
                      </h3>
                    </div>
                    <div className="flex items-center gap-4 text-[11px] font-mono">
                      <span className="text-stone-500 dark:text-stone-400">
                        Run A: {evalsA.length} evals
                      </span>
                      <span className="text-indigo-600 dark:text-indigo-400">
                        Run B: {evalsB.length} evals
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3.5">
                    {renderedCats.map(({ key, label, dataA, dataB }) => {
                      const maxCount = Math.max(
                        ...[1, 2, 3, 4, 5].map((val) => dataA?.dist[val] || 0),
                        ...[1, 2, 3, 4, 5].map((val) => dataB?.dist[val] || 0),
                        1,
                      );

                      const delta =
                        dataA && dataB ? dataB.avg - dataA.avg : null;

                      return (
                        <div
                          key={key}
                          className="p-3.5 bg-stone-50 dark:bg-stone-950 rounded-xl border border-stone-200 dark:border-stone-800/80 flex flex-col justify-between"
                        >
                          <div>
                            <div className="flex items-baseline justify-between gap-1">
                              <span
                                className="text-xs font-semibold text-stone-800 dark:text-stone-200 truncate"
                                title={label}
                              >
                                {label}
                              </span>
                              {delta !== null && (
                                <span
                                  className={`text-[11px] font-mono font-bold shrink-0 ${
                                    delta > 0
                                      ? "text-emerald-600 dark:text-emerald-400"
                                      : delta < 0
                                        ? "text-rose-600 dark:text-rose-400"
                                        : "text-stone-400"
                                  }`}
                                >
                                  {delta > 0
                                    ? `+${delta.toFixed(2)}`
                                    : delta.toFixed(2)}
                                </span>
                              )}
                            </div>

                            {/* A vs B Averages */}
                            <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-stone-200/60 dark:border-stone-800">
                              <div>
                                <div className="text-[10px] text-stone-400 uppercase font-medium">
                                  Run A
                                </div>
                                <div className="text-xs font-bold font-mono text-stone-700 dark:text-stone-300">
                                  {dataA ? dataA.avg.toFixed(2) : "—"}
                                </div>
                                <div className="text-[9px] text-stone-400">
                                  {dataA ? `${dataA.count} rated` : "no data"}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-[10px] text-indigo-500 uppercase font-medium">
                                  Run B
                                </div>
                                <div className="text-xs font-bold font-mono text-indigo-600 dark:text-indigo-400">
                                  {dataB ? dataB.avg.toFixed(2) : "—"}
                                </div>
                                <div className="text-[9px] text-stone-400">
                                  {dataB ? `${dataB.count} rated` : "no data"}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Side-by-side Mini Bars */}
                          <div className="space-y-1.5 mt-3 pt-2.5 border-t border-stone-200/60 dark:border-stone-800">
                            {[1, 2, 3, 4, 5].map((rating) => {
                              const cntA = dataA?.dist[rating] || 0;
                              const cntB = dataB?.dist[rating] || 0;
                              const pctA = (cntA / maxCount) * 100;
                              const pctB = (cntB / maxCount) * 100;

                              return (
                                <div
                                  key={rating}
                                  className="space-y-0.5 text-xs"
                                >
                                  <div className="flex items-center justify-between text-[10px]">
                                    <span className="font-mono text-stone-400">
                                      ★ {rating}
                                    </span>
                                    <span className="font-mono text-[9px] text-stone-400">
                                      <span className="text-stone-600 dark:text-stone-400">
                                        {cntA}
                                      </span>
                                      {" vs "}
                                      <span className="text-indigo-600 dark:text-indigo-400 font-semibold">
                                        {cntB}
                                      </span>
                                    </span>
                                  </div>
                                  <div className="space-y-1">
                                    <div className="bg-stone-200/70 dark:bg-stone-800/80 rounded h-1 overflow-hidden">
                                      <div
                                        className="bg-stone-500 dark:bg-stone-400 h-1 rounded transition-all duration-300"
                                        style={{ width: `${pctA}%` }}
                                        title={`Run A: ${cntA}`}
                                      />
                                    </div>
                                    <div className="bg-indigo-100/70 dark:bg-indigo-950/80 rounded h-1 overflow-hidden">
                                      <div
                                        className="bg-indigo-600 dark:bg-indigo-500 h-1 rounded transition-all duration-300"
                                        style={{ width: `${pctB}%` }}
                                        title={`Run B: ${cntB}`}
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Test Case Side-by-Side Comparison */}
            <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 overflow-hidden shadow-sm">
              <div className="p-4 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
                <h3 className="font-semibold text-stone-900 dark:text-stone-100 text-sm">
                  Test Case Breakdown & Output Comparison (
                  {comparison.case_comparisons.length})
                </h3>
              </div>

              <div className="divide-y divide-stone-100 dark:divide-stone-800">
                {comparison.case_comparisons.map((c: TestCaseComparison) => {
                  const caseResultA = runADetail?.case_results?.find(
                    (cr) => String(cr.test_case_id) === String(c.test_case_id),
                  );
                  const caseResultB = runBDetail?.case_results?.find(
                    (cr) => String(cr.test_case_id) === String(c.test_case_id),
                  );

                  // Extract prompt from case comparison or matched case results
                  const humanInput =
                    c.input_prompt ||
                    caseResultA?.input_prompt ||
                    caseResultB?.input_prompt ||
                    (caseResultA?.turns && caseResultA.turns.length > 0
                      ? caseResultA.turns[0].user_message
                      : null) ||
                    (caseResultB?.turns && caseResultB.turns.length > 0
                      ? caseResultB.turns[0].user_message
                      : null);

                  // Find evaluations matching threads or message/run IDs for case A & B
                  const evalsForCaseA = evalsA.filter((e) => {
                    if (
                      caseResultA?.thread_id &&
                      e.thread_id === caseResultA.thread_id
                    )
                      return true;
                    if (
                      caseResultA?.turns?.some(
                        (t) =>
                          (t.message_id && e.message_id === t.message_id) ||
                          (t.agent_run_id && e.run_id === t.agent_run_id),
                      )
                    )
                      return true;
                    return false;
                  });

                  const evalsForCaseB = evalsB.filter((e) => {
                    if (
                      caseResultB?.thread_id &&
                      e.thread_id === caseResultB.thread_id
                    )
                      return true;
                    if (
                      caseResultB?.turns?.some(
                        (t) =>
                          (t.message_id && e.message_id === t.message_id) ||
                          (t.agent_run_id && e.run_id === t.agent_run_id),
                      )
                    )
                      return true;
                    return false;
                  });

                  const calcAverageCategory = (
                    evals: EvaluationResponse[],
                    getter: (
                      e: EvaluationResponse,
                    ) => number | null | undefined,
                  ): number | null => {
                    const valid = evals
                      .map(getter)
                      .filter(
                        (v): v is number => typeof v === "number" && !isNaN(v),
                      );
                    if (valid.length === 0) return null;
                    return (
                      valid.reduce((acc, curr) => acc + curr, 0) / valid.length
                    );
                  };

                  const categories = [
                    {
                      label: "Accuracy",
                      shortLabel: "Acc",
                      getter: (e: EvaluationResponse) => e.accuracy_score,
                    },
                    {
                      label: "Adherence",
                      shortLabel: "Adh",
                      getter: (e: EvaluationResponse) =>
                        e.instruction_adherence_score,
                    },
                    {
                      label: "Tone",
                      shortLabel: "Tone",
                      getter: (e: EvaluationResponse) => e.tone_score,
                    },
                    {
                      label: "Tools",
                      shortLabel: "Tools",
                      getter: (e: EvaluationResponse) =>
                        e.tool_efficiency_score,
                    },
                  ];

                  const caseScoresA = categories.map((cat) => ({
                    ...cat,
                    score: calcAverageCategory(evalsForCaseA, cat.getter),
                  }));

                  const caseScoresB = categories.map((cat) => {
                    const score = calcAverageCategory(
                        evalsForCaseB,
                        cat.getter,
                      ),
                      scoreAVal = calcAverageCategory(
                        evalsForCaseA,
                        cat.getter,
                      );
                    const delta =
                      score !== null && scoreAVal !== null
                        ? score - scoreAVal
                        : null;
                    return {
                      ...cat,
                      score,
                      delta,
                    };
                  });

                  return (
                    <div key={c.test_case_id} className="p-5 space-y-4">
                      {/* Case Header */}
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm text-stone-900 dark:text-stone-100">
                          {c.title}
                        </span>
                      </div>

                      {/* Human Input Card */}
                      {humanInput && (
                        <div className="p-3.5 bg-amber-50/50 dark:bg-amber-950/20 rounded-lg border border-amber-200/80 dark:border-amber-900/50 text-xs">
                          <div className="flex items-center gap-1.5 mb-1.5 text-amber-700 dark:amber-400">
                            <User className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                              Human Input
                            </span>
                          </div>
                          <div className="text-stone-900 dark:text-stone-100 whitespace-pre-wrap font-sans leading-relaxed">
                            {humanInput}
                          </div>
                        </div>
                      )}

                      {/* Expected Output Card */}
                      {c.expected_output && (
                        <div className="p-3 bg-stone-50/70 dark:bg-stone-950/60 rounded-lg border border-stone-200 dark:border-stone-800/80 text-xs">
                          <span className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider block mb-1">
                            Expected Output
                          </span>
                          <div className="text-stone-800 dark:text-stone-200 whitespace-pre-wrap font-sans leading-relaxed">
                            {c.expected_output}
                          </div>
                        </div>
                      )}

                      {/* Side by side outputs for Run A and Run B */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Run A result */}
                        <div className="p-3.5 bg-stone-50 dark:bg-stone-950 rounded-lg border border-stone-200 dark:border-stone-800/80 text-xs space-y-2">
                          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200/60 dark:border-stone-800 pb-1.5">
                            <span className="text-stone-600 dark:text-stone-400 font-semibold uppercase tracking-wider text-[10px]">
                              Run A Output (Baseline)
                            </span>
                            <div className="flex flex-wrap items-center gap-2 font-mono text-[10px]">
                              {caseScoresA.map((cs) => (
                                <div
                                  key={cs.label}
                                  className="flex items-center gap-1 bg-stone-100 dark:bg-stone-900 px-1.5 py-0.5 rounded text-stone-600 dark:text-stone-400"
                                  title={`${cs.label} Score`}
                                >
                                  <span className="text-stone-400 uppercase text-[9px] font-sans">
                                    {cs.shortLabel}:
                                  </span>
                                  <span className="font-bold text-stone-700 dark:text-stone-300">
                                    {cs.score !== null
                                      ? cs.score.toFixed(1)
                                      : "—"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="text-stone-800 dark:text-stone-200 whitespace-pre-wrap font-sans min-h-[60px] leading-relaxed">
                            {c.run_a_output || (
                              <span className="text-stone-400 italic">
                                No output recorded
                              </span>
                            )}
                          </div>
                          {c.run_a_notes && (
                            <div className="pt-2 border-t border-stone-200/50 dark:border-stone-800 text-stone-500 italic text-[11px]">
                              Notes: {c.run_a_notes}
                            </div>
                          )}
                        </div>

                        {/* Run B result */}
                        <div className="p-3.5 bg-indigo-50/30 dark:bg-indigo-950/20 rounded-lg border border-indigo-100 dark:border-indigo-900/40 text-xs space-y-2">
                          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-indigo-100/60 dark:border-indigo-900/50 pb-1.5">
                            <span className="text-indigo-600 dark:text-indigo-400 font-semibold uppercase tracking-wider text-[10px]">
                              Run B Output (Candidate)
                            </span>
                            <div className="flex flex-wrap items-center gap-2 font-mono text-[10px]">
                              {caseScoresB.map((cs) => (
                                <div
                                  key={cs.label}
                                  className="flex items-center gap-1 bg-indigo-50/80 dark:bg-indigo-950/50 px-1.5 py-0.5 rounded text-indigo-700 dark:text-indigo-300 border border-indigo-100/80 dark:border-indigo-900/50"
                                  title={`${cs.label} Score (Candidate vs Baseline)`}
                                >
                                  <span className="text-indigo-400 dark:text-indigo-500 uppercase text-[9px] font-sans">
                                    {cs.shortLabel}:
                                  </span>
                                  <span className="font-bold text-indigo-800 dark:text-indigo-200">
                                    {cs.score !== null
                                      ? cs.score.toFixed(1)
                                      : "—"}
                                  </span>
                                  {cs.delta !== null && (
                                    <span
                                      className={`text-[9px] font-bold px-1 rounded ${
                                        cs.delta > 0
                                          ? "text-emerald-700 dark:text-emerald-300 bg-emerald-100/70 dark:bg-emerald-950/80"
                                          : cs.delta < 0
                                            ? "text-rose-700 dark:text-rose-300 bg-rose-100/70 dark:bg-rose-950/80"
                                            : "text-stone-500 bg-stone-100 dark:bg-stone-800"
                                      }`}
                                    >
                                      {cs.delta > 0
                                        ? `+${cs.delta.toFixed(1)}`
                                        : cs.delta < 0
                                          ? cs.delta.toFixed(1)
                                          : "0.0"}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="text-stone-900 dark:text-stone-100 whitespace-pre-wrap font-sans min-h-[60px] leading-relaxed">
                            {c.run_b_output || (
                              <span className="text-stone-400 italic">
                                No output recorded
                              </span>
                            )}
                          </div>
                          {c.run_b_notes && (
                            <div className="pt-2 border-t border-indigo-100/60 dark:border-indigo-900/50 text-indigo-700/80 dark:text-indigo-300/80 italic text-[11px]">
                              Notes: {c.run_b_notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-12 text-center text-sm text-stone-400 bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800">
            Select two different completed runs above to generate an A/B delta
            comparison.
          </div>
        )}
      </div>
    </div>
  );
}
